import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { useScrollEnds } from '../../hooks/useScrollEnds';
import { usePages } from '../../contexts/SiteContext';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { quietLink, sectionHeading } from '../../ui/sections';
import type { Product } from '../../core/types/catalog';
type Props = {
  products: Product[];
  total: number;
};
const arrowBtn = 'flex h-12 w-12 items-center justify-center rounded-full border border-ink/20 text-ink transition-[background-color,color,border-color,opacity] duration-150 ease-soft hover:border-ink hover:bg-ink hover:text-shell disabled:pointer-events-none disabled:opacity-30';

/**
 * "Wat ik het vaakst meegeef": de salonfavorieten als plank waar je langs schuift. Vegen,
 * scrollen of de pijlen (die uitgaan aan het begin en eind); een haarlijn toont hoe ver je
 * bent. De laatste kaart steekt op elk scherm half uit, zodat je ziet dat er meer staat.
 */
export function HomeShelf({
  products,
  total
}: Props) {
  const pages = usePages();
  const railRef = useRef<HTMLUListElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollXProgress
  } = useScroll({
    container: railRef
  });
  const {
    atStart,
    atEnd
  } = useScrollEnds(railRef);
  const copy = pages.home.bestsellers;
  const nudge = (dir: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: dir * rail.clientWidth * 0.8,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  return <section className="overflow-hidden bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-bestsellers" data-id="element-7494" data-mp="17jn322funad3">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7495" data-mp="12ktnm4h059aa">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-7496" data-mp="xknraazseylv">
          <div className="max-w-2xl" data-id="element-7497" data-mp="nwa72zelno53">
            <h2 id="home-bestsellers" className={`${sectionHeading} text-ink`} data-id="element-7498" data-mp="nv8p0az621dv">
              <HeadingText heading={copy.heading} data-id="element-7499" data-mp="sw4uuu32vxlx" />
            </h2>
            {copy.body && <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7500" data-mp="evu2fmm4eayn">{fill(copy.body)}</p>}
          </div>
          <div className="flex items-center gap-6" data-id="element-7501" data-mp="ixytqep3ivi4">
            {copy.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-7502" data-mp="16lzyrdn2ftss">
                {fill(copy.cta, {
              count: total
            })}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7503" data-mp="1bkjntmwyhlpd" />
              </Link>}
            <div className="hidden gap-2 sm:flex" data-id="element-7504" data-mp="xtxzvjsnalkm">
              <button type="button" onClick={() => nudge(-1)} disabled={atStart} aria-label="Vorige producten" aria-controls="home-shelf" className={arrowBtn} data-id="element-7505" data-mp="11v9q9rjcfatv">
                <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7506" data-mp="1pj9kx2jrzldq" />
              </button>
              <button type="button" onClick={() => nudge(1)} disabled={atEnd} aria-label="Volgende producten" aria-controls="home-shelf" className={arrowBtn} data-id="element-7507" data-mp="3f2hsvqksd3pw">
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7508" data-mp="3a2vf9g11plgk" />
              </button>
            </div>
          </div>
        </div>

        <ul id="home-shelf" ref={railRef} aria-label="Salonfavorieten"
      // Ruimte boven en onder: een kaart die bij aanwijzen optilt (met schaduw), valt niet buiten de rail.
      className="-mx-6 mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-6 px-6 pb-6 pt-2 [scrollbar-width:none] sm:gap-6 lg:-mx-10 lg:mt-12 lg:scroll-px-10 lg:px-10 [&::-webkit-scrollbar]:hidden" data-id="element-7509" data-mp="3jsofjkvqme5i">
          {products.map((p, i) =>
        // Kort na elkaar binnen (40 ms per kaart); alleen de eerste vier tellen mee, de rest staat buiten beeld.
        <motion.li key={p.id} className="w-[68%] shrink-0 snap-start sm:w-[40%] lg:w-[calc((100%-4.5rem)/3.5)]" initial={reduce ? false : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '0px 0px -10% 0px'
        }} transition={{
          duration: 0.3,
          delay: Math.min(i, 4) * 0.04,
          ease
        }} data-id="element-7510" data-mp="gcrblto662fm" data-mpi={i}>
              <div className="h-full transition-[transform,box-shadow] duration-150 ease-soft [@media(hover:hover)]:hover:-translate-y-1 [@media(hover:hover)]:hover:shadow-lift" data-id="element-7511" data-mp="301m2b7pnnsad">
                <ProductCard product={p} data-id="element-7512" data-mp="39y77pu5l75eq" />
              </div>
            </motion.li>)}
        </ul>

        {!(atStart && atEnd) && <div aria-hidden="true" className="mt-10 h-px w-full bg-ink/15" data-id="element-7513" data-mp="3epyxw3ktz94c">
            <motion.div style={{
          scaleX: scrollXProgress
        }} className="h-px origin-left bg-ink" data-id="element-7514" data-mp="3ipzffx3880fm" />
          </div>}
      </div>
    </section>;
}